// @ts-nocheck
// Vitrine de descoberta de clubes públicos — dados REAIS de /api/clubs/explore.
// Capa = imagem custom (se houver) ou pôster de gradiente do hue. Adaptativo:
// hero+trilhas quando há muitos clubes, grade limpa quando poucos.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../../services/apiV2';
import { Icon } from '../icons';
import { toast } from '../toast';
import { AcervoMosaico, AcervoLivroScreen } from '../acervo';
import { semAcento } from '../note-card';
import { BookCover } from '../common';

// ── Gêneros ──
// Saíram dos livros que EXISTEM no acervo, não de uma lista genérica de
// livraria. A barra antiga (Fantasia, Romance, Terror) filtrava `clubs.genre`,
// que está NULO nos 26 clubes do banco — nenhum tem gênero preenchido. Ou seja,
// tocar num chip esvaziava a tela. Agora ela filtra o acervo, que é o conteúdo
// que de fato existe, e cada chip tem no mínimo 5 livros atrás dele.
//
// "Literatura" entrou agora que a estante do Wesley trouxe ficção — antes ela
// não existia aqui, e chip que filtra pra vazio é o problema que a barra
// antiga tinha. A regra continua: gênero só existe quando há livro atrás dele.
const GENRES = ['Tudo', 'Investimentos', 'Negócios', 'História', 'Finanças', 'Mentalidade', 'Biografias', 'Desenvolvimento pessoal', 'Literatura'];

const fmtNum = (n: number) => n >= 1000 ? (n / 1000).toFixed(1).replace('.0', '') + 'k' : '' + (n || 0);

// ── Arrastar a fila com o mouse ──
// No celular o dedo já arrasta sozinho. No desktop não: a roda do mouse rola
// na vertical, então uma fila horizontal fica parada e o que passa da borda
// vira invisível. Era esse o motivo de a barra de gêneros não andar.
//
// Só entra pra ponteiro que não é toque — no toque, mexer no scroll na mão
// atrapalha o embalo nativo, que é melhor que qualquer imitação.
function useArrastarLado() {
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let pressionado = false, arrastou = false, xInicial = 0, scrollInicial = 0;

    const desceu = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      pressionado = true; arrastou = false;
      xInicial = e.clientX; scrollInicial = el.scrollLeft;
    };
    const moveu = (e: PointerEvent) => {
      if (!pressionado) return;
      const d = e.clientX - xInicial;
      // Folga de 4px antes de virar arrasto: sem ela, um clique com a mão um
      // pouco trêmula deixaria de selecionar o gênero.
      if (!arrastou && Math.abs(d) > 4) arrastou = true;
      if (arrastou) { el.scrollLeft = scrollInicial - d; e.preventDefault(); }
    };
    const soltou = () => { pressionado = false; };
    // O clique só é engolido se houve arrasto de verdade.
    const clicou = (e: MouseEvent) => {
      if (!arrastou) return;
      e.preventDefault(); e.stopPropagation();
      arrastou = false;
    };

    el.addEventListener('pointerdown', desceu);
    window.addEventListener('pointermove', moveu);
    window.addEventListener('pointerup', soltou);
    el.addEventListener('click', clicou, true);
    return () => {
      el.removeEventListener('pointerdown', desceu);
      window.removeEventListener('pointermove', moveu);
      window.removeEventListener('pointerup', soltou);
      el.removeEventListener('click', clicou, true);
    };
  }, []);
  return ref;
}

// Gradiente do hue (quando não há capa custom). Poster intencional.
function hueGradient(hue: number) {
  const h = hue ?? 28;
  return `radial-gradient(120% 110% at 70% 12%, hsl(${h} 55% 42%) 0%, hsl(${h} 60% 24%) 48%, hsl(${(h + 12) % 360} 65% 12%) 100%)`;
}

// ── Capa do clube (imagem custom ou pôster de gradiente) ──
function ClubCover({ club, kicker, showTitle = true, landscape = false }: any) {
  // landscape (hero 4:3) prioriza o banner 4:3; retrato (cards 2:3) usa o poster
  const src = landscape ? (club.customBannerUrl || club.customCoverUrl) : club.customCoverUrl;
  const hasImg = !!src;
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: hasImg ? '#1a1a1a' : hueGradient(club.hue) }}>
      {hasImg && <img src={src} alt={club.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
      {/* scrim pra legibilidade do título */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.1) 42%, transparent 62%)' }} />
      {!hasImg && <div style={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 60px rgba(0,0,0,0.3)' }} />}
      {showTitle && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '14px 13px 13px', display: 'flex', flexDirection: 'column', gap: 5 }}>
          {kicker && <div className="mono" style={{ fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)', fontWeight: 700 }}>{kicker}</div>}
          <div className="serif" style={{ fontSize: 19, lineHeight: 1.05, color: '#fff', fontWeight: 600, textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>{club.name}</div>
        </div>
      )}
    </div>
  );
}

function Badges({ club }: any) {
  const items = [];
  if (club.yourRole === 'owner') items.push({ t: 'Seu clube', c: 'var(--ember)' });
  else if (club.yourRole) items.push({ t: 'Membro', c: 'var(--sage, #9EB083)' });
  if (club.full) items.push({ t: 'Lotado', c: 'var(--ink-3)' });
  else if (club.almostFull) items.push({ t: 'Quase lotando', c: 'var(--plum, #C08CAE)' });
  if (club.isNew && !club.yourRole) items.push({ t: 'Novo', c: 'var(--ember-2, #F29A60)' });
  if (!items.length) return null;
  return (
    <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', flexWrap: 'wrap', gap: 5, zIndex: 3 }}>
      {items.slice(0, 2).map((b, i) => (
        <span key={i} className="mono" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '3px 7px', borderRadius: 999, background: 'rgba(8,6,5,0.6)', backdropFilter: 'blur(6px)', color: b.c }}>{b.t}</span>
      ))}
    </div>
  );
}

// Card pôster (capa 2:3 + membros + livro)
function PosterCard({ club, onOpen, big }: any) {
  return (
    <button onClick={() => onOpen(club)} style={{ display: 'block', textAlign: 'left', background: 'none', border: 'none', padding: 0, width: '100%', cursor: 'pointer' }}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '2 / 3', borderRadius: 14, overflow: 'hidden', boxShadow: '0 10px 28px -10px rgba(0,0,0,0.5)' }}>
        <ClubCover club={club} kicker={club.tier === 'template' ? null : club.genre} />
        <Badges club={club} />
      </div>
      <div style={{ padding: '8px 2px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-3)' }}>
          <Icon name="users" size={12} color="var(--ink-3)" />{fmtNum(club.members)} {club.members === 1 ? 'membro' : 'membros'}
          {club.book?.title && <><span>·</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{club.book.title}</span></>}
        </div>
      </div>
    </button>
  );
}

function SectionHead({ title, sub }: any) {
  return (
    <div style={{ padding: '0 18px 10px' }}>
      <h2 className="serif" style={{ fontSize: 21, color: 'var(--ink)', margin: 0, lineHeight: 1.1 }}>{title}</h2>
      {sub && <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Detail sheet — busca o clube completo + join real
function DetailSheet({ club, session, onClose, onOpenClub, onUpgrade }: any) {
  const [full, setFull] = useState<any>(null);
  const [joining, setJoining] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api.getClub(session, club.id).then((c: any) => { if (alive) setFull(c); }).catch(() => {});
    return () => { alive = false; };
  }, [club.id, session]);
  const c = full || club;
  const isMember = !!c.yourRole;
  const cap = c.capacity ?? club.capacity;
  const lotado = c.full ?? club.full;
  const pct = cap ? Math.min(100, Math.round((c.members / cap) * 100)) : null;
  const modeInfo = (c.mode === 'group-read')
    ? { t: 'Leitura em grupo', d: 'Todo mundo no mesmo livro, no mesmo ritmo.' }
    : { t: 'Leitura avulsa', d: 'Cada um lê o seu — a roda é pra trocar ideia.' };
  const book = c.book || club.book;

  const join = async () => {
    if (isMember) { onOpenClub(c.id); return; }
    setJoining(true); setErr(null);
    try {
      await api.joinClub(session, c.id);
      onOpenClub(c.id);
    } catch (e: any) {
      const msg = (e?.message || '') + '';
      // Bateu o teto de clubes do plano (free = 2) → paywall limpo, não erro cru.
      if (msg.includes('plan_required') || msg.includes('plan_limit_reached')) {
        setJoining(false);
        if (onUpgrade) { onClose(); onUpgrade(); return; }
      }
      if (msg.includes('club_member_limit_reached')) {
        setErr('Esse clube atingiu o máximo de membros pro plano do líder.');
      } else {
        setErr(msg || 'Não foi possível entrar agora.');
      }
      setJoining(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(8,6,5,0.66)', backdropFilter: 'blur(3px)', animation: 'e2FadeIn 0.2s ease both' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, maxHeight: '94vh', background: 'var(--paper-2)', borderRadius: '24px 24px 0 0', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 -20px 60px rgba(0,0,0,0.5)', animation: 'e2SheetUp 0.34s cubic-bezier(0.2,0.9,0.3,1) both' }}>
        <div style={{ position: 'relative', width: '100%', height: 240, flexShrink: 0 }}>
          <ClubCover club={{ ...club, ...c }} kicker={c.genre || club.genre} showTitle={false} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, var(--paper-2) 2%, transparent 55%)' }} />
          <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, width: 34, height: 34, borderRadius: '50%', background: 'rgba(8,6,5,0.5)', backdropFilter: 'blur(6px)', display: 'grid', placeItems: 'center', zIndex: 4 }}><Icon name="close" size={18} color="#fff" /></button>
          <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', width: 38, height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.4)', zIndex: 4 }} />
        </div>
        <div style={{ padding: '0 22px 18px', overflowY: 'auto', marginTop: -6 }}>
          <h2 className="serif" style={{ fontSize: 26, lineHeight: 1.05, color: 'var(--ink)', margin: '0 0 8px' }}>{c.name}</h2>
          {c.description && <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink-2)', margin: '0 0 18px' }}>{c.description}</p>}

          <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
            {[{ label: 'membros', val: fmtNum(c.members) }, ...(c.activeToday != null ? [{ label: 'ativos hoje', val: fmtNum(c.activeToday) }] : []), ...(pct != null ? [{ label: 'lotação', val: pct + '%' }] : [])].map((s: any, i: number) => (
              <div key={i} style={{ flex: 1, background: 'var(--paper-3)', borderRadius: 14, padding: '12px 12px 11px' }}>
                <div className="serif" style={{ fontSize: 20, color: 'var(--ink)' }}>{s.val}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {pct != null && (
            <>
              <div style={{ height: 6, borderRadius: 999, background: 'var(--paper-3)', overflow: 'hidden', marginBottom: 4 }}>
                <div style={{ width: pct + '%', height: '100%', borderRadius: 999, background: 'var(--ember)' }} />
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 18 }}>{cap - c.members > 0 ? `${fmtNum(cap - c.members)} vagas restantes` : 'Clube lotado'}</div>
            </>
          )}

          {book?.title && (
            <>
              <div className="mono" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 10 }}>Lendo agora</div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 18 }}>
                <div style={{ width: 48, height: 70, borderRadius: 6, flexShrink: 0, background: book.coverUrl ? `url(${book.coverUrl}) center/cover` : hueGradient(book.coverHue ?? 28), boxShadow: '0 6px 16px rgba(0,0,0,0.4)' }} />
                <div>
                  <div className="serif" style={{ fontSize: 16, color: 'var(--ink)', lineHeight: 1.15 }}>{book.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>{book.author}</div>
                </div>
              </div>
            </>
          )}

          <div style={{ padding: '13px 14px', borderRadius: 14, background: 'var(--paper-3)', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}><Icon name="book" size={15} color="var(--ember)" />{modeInfo.t}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.35 }}>{modeInfo.d}</div>
          </div>

          {(c.admin?.name || c.ownerName || club.ownerName) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4, fontSize: 12.5, color: 'var(--ink-3)' }}>
              <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, var(--ember), var(--plum, #7c1606))', flexShrink: 0 }} />
              Liderado por <b style={{ color: 'var(--ink)' }}>{c.admin?.name || c.ownerName || club.ownerName}</b>
            </div>
          )}
          {err && <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: 'rgba(192,58,42,0.1)', color: '#C03A2A', fontSize: 13, textAlign: 'center' }}>{err}</div>}
        </div>

        <div style={{ padding: '14px 22px calc(16px + env(safe-area-inset-bottom,0px))', flexShrink: 0, background: 'linear-gradient(0deg, var(--paper-2) 60%, transparent)', borderTop: '1px solid var(--line)' }}>
          {/* `lotado` COM RESERVA, como o `cap` logo acima ja fazia.
              `full` so existe na resposta da LISTAGEM (/api/clubs/explore). A
              folha de detalhes busca /api/clubs/:id, que nao traz esse campo —
              entao, no instante em que a resposta chegava, `c.full` virava
              undefined e o botao destravava SOZINHO: de "Clube lotado" para
              "Entrar no clube", sem ninguem tocar em nada. A pessoa tocava,
              via "Entrando..." e so ai recebia o erro de lotacao — descobrindo
              pelo erro o que a lista ja sabia antes de a tela abrir. */}
          <button onClick={join} disabled={joining || (lotado && !isMember)} style={{
            width: '100%', padding: '15px', borderRadius: 16, border: 'none', cursor: 'pointer',
            fontFamily: 'Instrument Serif, Georgia, serif', fontWeight: 600, fontSize: 17,
            background: isMember ? 'var(--paper-3)' : (lotado ? 'var(--paper-3)' : 'var(--ember)'),
            color: isMember ? 'var(--ink)' : (lotado ? 'var(--ink-3)' : '#fff'),
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: isMember || lotado ? 'none' : '0 10px 28px -8px rgba(199,83,26,0.5)',
            opacity: joining ? 0.7 : 1,
          }}>
            {joining ? 'Entrando…' : isMember ? <><Icon name="check" size={18} color="var(--ink)" /> Abrir clube</> : lotado ? 'Clube lotado' : 'Entrar no clube'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Avatar simples no tema da Explorar (foto ou iniciais) — pessoas não usam o
// PersonRow das sheets sociais (tema escuro), pra manter o visual da vitrine.
function MiniAvatar({ name, url, size = 46 }: any) {
  const initials = (name || '?').trim().split(/\s+/).slice(0, 2).map((w: string) => w[0]).join('').toUpperCase();
  return url
    ? <img src={url} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: 'var(--paper-3)' }} />
    : <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', background: 'var(--paper-3)', color: 'var(--ink-3)', fontWeight: 700, fontSize: size * 0.36 }}>{initials}</div>;
}

// Resultados de PESSOAS — só aparecem quando se pesquisa (≥2 letras, com debounce).
// Pessoas NÃO ficam listadas na vitrine (privacidade); só surgem na busca.
function PeopleResults({ session, q, onOpenProfile }: any) {
  const [people, setPeople] = useState<any>(null); // null=termo curto, undefined=carregando, []=vazio
  const [busy, setBusy] = useState<any>({});
  useEffect(() => {
    const term = (q || '').trim();
    if (term.length < 2) { setPeople(null); return; }
    let alive = true;
    // Mantém a lista atual visível enquanto re-busca (só mostra "Buscando…" se ainda não há lista) — sem flicker a cada tecla.
    setPeople((prev: any) => (Array.isArray(prev) && prev.length) ? prev : undefined);
    const t = setTimeout(() => {
      api.searchUsers(session, term).then((r: any) => { if (alive) setPeople(r.users || []); }).catch(() => { if (alive) setPeople([]); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [q, session]);

  const toggle = async (p: any) => {
    if (busy[p.id]) return;
    setBusy((b: any) => ({ ...b, [p.id]: true }));
    const doFollow = !(p.isFollowing || p.requested);
    try {
      const r: any = await (doFollow ? api.followUser : api.unfollowUser)(session, p.id);
      setPeople((ppl: any) => (ppl || []).map((x: any) => x.id === p.id ? { ...x, isFollowing: !!r?.following, requested: !!r?.requested } : x));
    } catch { /* mantém estado */ }
    finally { setBusy((b: any) => ({ ...b, [p.id]: false })); }
  };

  if (people === null) return null;
  if (people === undefined) {
    return <div style={{ paddingBottom: 10 }}><SectionHead title="Pessoas" /><div style={{ padding: '4px 18px 8px', color: 'var(--ink-3)', fontSize: 13 }}>Buscando…</div></div>;
  }
  if (!people.length) return null; // sem pessoas → some a seção (clubes continuam)

  return (
    <div style={{ paddingBottom: 12 }}>
      <SectionHead title="Pessoas" />
      <div style={{ padding: '0 18px' }}>
        {people.map((p: any) => {
          const ghost = p.isFollowing || p.requested;
          const label = p.isFollowing ? 'Seguindo' : p.requested ? 'Solicitado' : 'Seguir';
          return (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0' }}>
              <button onClick={() => onOpenProfile && onOpenProfile(p.id)} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
                <MiniAvatar name={p.name} url={p.avatarUrl} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                    {p.isPrivate && <Icon name="lock" size={12} color="var(--ink-3)" />}
                  </div>
                  {p.username && <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>@{p.username}</div>}
                  {p.bio && <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.bio}</div>}
                </div>
              </button>
              <button onClick={() => toggle(p)} disabled={busy[p.id]} style={{
                flexShrink: 0, padding: '7px 15px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 5,
                border: ghost ? '1px solid var(--line)' : 'none',
                background: ghost ? 'var(--paper-2)' : 'var(--ember)',
                color: ghost ? 'var(--ink)' : '#fff',
                opacity: busy[p.id] ? 0.6 : 1,
              }}>
                {p.requested && <Icon name="clock" size={12} color="var(--ink-3)" />}{label}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const ExploreScreen: React.FC<{
  session: any;
  onBack: () => void;
  onOpenClub: (clubId: string) => void;
  onOpenProfile?: (userId: string) => void;
  onUpgrade?: () => void;
  /** Avisa que a estante mudou (um livro entrou em "quero ler"). Sem isto o
   *  livro existia no servidor e nao na lista do app. */
  onAcervoMudou?: () => void;
  /** A estante da pessoa: a busca acha os livros dela também (QA-25). */
  minhaEstante?: any[];
  onOpenBook?: (b: any) => void;
}> = ({ session, onBack, onOpenClub, onOpenProfile, onUpgrade, onAcervoMudou, minhaEstante = [], onOpenBook }) => {
  const [clubs, setClubs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [paywalled, setPaywalled] = useState(false);
  const [query, setQuery] = useState('');
  const [genre, setGenre] = useState('Tudo');
  const [open, setOpen] = useState<any>(null);
  // Livro do acervo aberto. Camada da Explorar, não rota: voltar tem que
  // devolver a lista exatamente onde estava.
  const [livroAcervo, setLivroAcervo] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [jaEntraram, setJaEntraram] = useState<Set<string>>(new Set());
  const faixaGeneros = useArrastarLado();

  useEffect(() => {
    let alive = true;
    setLoading(true); setPaywalled(false);
    api.exploreClubs(session, { limit: 50, sort: 'popular' })
      .then((r: any) => { if (alive) { setClubs(r.clubs || []); setLoading(false); } })
      .catch((e: any) => {
        if (!alive) return;
        if ((e?.message || '').includes('plan_required') || (e?.message || '').includes('402')) setPaywalled(true);
        setLoading(false);
      });
    return () => { alive = false; };
  }, [session]);

  // "Quero ler" — o motivo de a estante curada existir. O livro entra na
  // prateleira de desejo com capa, páginas e ISBN já preenchidos: quem chegou
  // aqui não precisa digitar nada, que é justamente o atrito que a curadoria
  // veio tirar. Entra como `wishlist`, não como leitura em andamento — querer
  // ler não é começar, e assumir isso sujaria a chama de quem não abriu o livro.
  const querLer = useCallback(async (livro: any) => {
    if (salvando) return;
    // O servidor não trava livro repetido — dois toques criariam duas cópias
    // na estante. Guardo o que já entrou nesta sessão, que cobre o caso real
    // (bater duas vezes, ou voltar no mesmo livro logo depois).
    if (jaEntraram.has(livro.slug)) {
      toast.info('Esse já está na sua lista');
      setLivroAcervo(null);
      return;
    }
    setSalvando(true);
    try {
      await api.createBook(session, {
        title: livro.titulo,
        author: livro.autor || '',
        totalPages: livro.paginas || 0,
        status: 'wishlist',
        // O servidor BAIXA essa capa, então precisa de endereço absoluto:
        // com o caminho relativo do catálogo o fetch dele quebra e o livro
        // nasceria sem capa — logo aqui, onde a capa é o que atraiu a pessoa.
        coverSourceUrl: livro.capa ? new URL(livro.capa, window.location.origin).href : null,
        isbn: livro.isbn13 || livro.isbn10 || null,
      });
      setJaEntraram((s) => new Set(s).add(livro.slug));
      // O ACERVO DO CLIENTE PRECISA SABER.
      // Sem este aviso, o livro existia no servidor e nao na lista do app —
      // e tocar nele no perfil abria uma tela sem livro. A correcao de fundo
      // esta em `openBookById`, que busca quando nao acha; este aviso evita a
      // busca extra no caminho comum.
      if (onAcervoMudou) { try { onAcervoMudou(); } catch (e) {} }
      toast.success('Adicionado à sua estante — quando tiver o livro em mãos, é só começar');
      setLivroAcervo(null);          // devolve pra estante, que é onde ele estava
    } catch (e: any) {
      const m = String(e?.message || '');
      // Livro repetido não é erro pro leitor: ele já fez o que queria fazer.
      if (/duplicat|already|existe|409/i.test(m)) toast.info('Esse já está na sua lista');
      // O servidor manda uma frase em português quando sabe o motivo (400).
      // Engolir isso atrás de "Não deu pra guardar agora" foi o que escondeu,
      // por tempo indeterminado, que um terço do Acervo não entrava na estante:
      // o leitor não tinha o que fazer com a mensagem, e nós não tínhamos o
      // que investigar sem ir no log do servidor.
      else if (e?.status === 400 && m && !/^HTTP /.test(m)) toast.error(m);
      else toast.error('Não deu pra guardar agora');
    } finally {
      setSalvando(false);
    }
  }, [session, salvando, jaEntraram]);

  // Sem acento dos dois lados — "decadas" tem de achar "Decadas de Leitura".
  const q = semAcento(query);
  // A busca continua sendo dos CLUBES. O gênero saiu daqui: `clubs.genre` está
  // nulo nos 26 clubes do banco, então filtrar por ele só produzia tela vazia.
  const filtered = useMemo(() => clubs.filter((c) => {
    if (!q) return true;
    return semAcento(c.name).includes(q) || semAcento(c.genre).includes(q) || semAcento(c.book?.title).includes(q) || semAcento(c.book?.author).includes(q);
  }), [clubs, genre, q]);

  // Duas coisas diferentes, que antes estavam no mesmo interruptor: buscar
  // mexe com os clubes, escolher gênero mexe com a estante.
  const filtering = !!q;
  const porGenero = genre !== 'Tudo';
  const enough = clubs.length >= 6;
  const heroClub = clubs.find((c) => c.tier === 'hero') || clubs[0];
  const trending = [...clubs].sort((a, b) => (b.members || 0) - (a.members || 0)).slice(0, 8);
  const news = clubs.filter((c) => c.isNew);

  if (livroAcervo) {
    return (
      <AcervoLivroScreen
        slug={livroAcervo}
        session={session}
        onVoltar={() => setLivroAcervo(null)}
        onAdicionarNaEstante={querLer}
      />
    );
  }

  return (
    <div className="scroll" style={{ minHeight: '100vh', paddingBottom: 40 }}>
      {/* header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 20, background: 'color-mix(in oklab, var(--paper) 88%, transparent)', backdropFilter: 'blur(14px)', borderBottom: '1px solid var(--line)', paddingTop: 'env(safe-area-inset-top,0px)' }}>
        <div style={{ padding: '14px 18px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--paper-2)', border: '1px solid var(--line)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name="chevron-left" size={18} /></button>
          {/* Só "Descobrir". O rótulo "Clubes públicos" prometia menos do que a
              tela entrega: aqui também mora a estante curada, e desde que os
              gêneros passaram a filtrar livros, quem chega por eles não veio
              atrás de clube nenhum. */}
          <h1 className="serif" style={{ fontSize: 26, color: 'var(--ink)', margin: 0, lineHeight: 1 }}>Descobrir</h1>
        </div>
        <div style={{ padding: '12px 18px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 14px', borderRadius: 13, background: 'var(--paper-2)', border: '1px solid var(--line)' }}>
            <Icon name="search" size={17} color="var(--ink-3)" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar seus livros, pessoas, clubes" style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 14.5 }} />
            {query && <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none' }}><Icon name="close" size={15} color="var(--ink-3)" /></button>}
          </div>
        </div>
        {/* Fila única, arrastável. O véu à direita é a dica de que a fila
            continua: sem ele o último chip encosta na borda e a fila parece
            terminar ali. Ele não recebe clique, senão comeria o chip embaixo. */}
        <div style={{ position: 'relative' }}>
          <div ref={faixaGeneros} className="scroll-x" style={{
            display: 'flex', flexWrap: 'nowrap', gap: 8, padding: '0 18px 13px',
            cursor: 'grab',
          }}>
            {GENRES.map((g) => {
              const on = genre === g;
              return <button key={g} onClick={() => setGenre(g)} style={{ flexShrink: 0, padding: '7px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', background: on ? 'var(--ember)' : 'var(--paper-2)', color: on ? '#fff' : 'var(--ink-3)', border: `1px solid ${on ? 'var(--ember)' : 'var(--line)'}` }}>{g}</button>;
            })}
          </div>
          {/* O véu termina na cor DO HEADER, não em `--paper` puro: o header é
              translúcido com desfoque, e um véu opaco viraria um remendo
              visível por cima dele. */}
          <div aria-hidden style={{
            position: 'absolute', top: 0, right: 0, bottom: 13, width: 34,
            pointerEvents: 'none',
            background: 'linear-gradient(90deg, transparent, color-mix(in oklab, var(--paper) 88%, transparent) 80%)',
          }}/>
        </div>
      </header>

      {/* Busca de PESSOAS — independente do estado dos clubes: aparece sempre que
          há termo. Antes ficava presa dentro do branch `filtering`, que só é
          alcançado com clubes > 0 — então com 0 clubes públicos não dava pra
          pesquisar usuário nenhum. */}
      {/* NA SUA ESTANTE (pente fino, QA-25). O campo diz "Buscar SEUS livros",
          mas a busca só olhava o acervo curado: "Dom Casmurro", que estava na
          estante da própria pessoa, dava "Nenhum livro do acervo". Filtro
          local por título e autor, sem acento, no topo. */}
      {q.length >= 2 && (() => {
        const meus = (minhaEstante || []).filter((b: any) => semAcento(b.title).includes(q) || semAcento(b.author).includes(q)).slice(0, 6);
        if (!meus.length) return null;
        return (
          <div data-na-estante="1" style={{ padding: '6px 20px 14px' }}>
            <div className="mono" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 10 }}>Na sua estante</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {meus.map((b: any) => (
                <button key={b.id} onClick={() => onOpenBook && onOpenBook(b)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: 8, borderRadius: 12, textAlign: 'left',
                  background: 'var(--paper-2)', border: '1px solid var(--line)', cursor: 'pointer',
                }}>
                  <BookCover book={b} width={34} height={50} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="serif" style={{ fontSize: 15, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.author}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {q && <PeopleResults session={session} q={query} onOpenProfile={onOpenProfile} />}

      {paywalled ? (
        <div style={{ display: 'grid', placeItems: 'center', textAlign: 'center', padding: '60px 30px', gap: 14 }}>
          <div style={{ width: 60, height: 60, borderRadius: 18, background: 'var(--ember-soft, #3C2A1C)', display: 'grid', placeItems: 'center' }}><Icon name="lock" size={26} color="var(--ember)" /></div>
          <div className="serif" style={{ fontSize: 24, color: 'var(--ink)' }}>Descubra clubes públicos</div>
          <div style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.5, maxWidth: 280 }}>Explorar e entrar em clubes públicos faz parte dos planos pagos. Assine pra abrir tudo.</div>
          {onUpgrade && <button onClick={onUpgrade} style={{ marginTop: 8, padding: '14px 28px', borderRadius: 14, background: 'var(--ember)', color: '#fff', fontSize: 15, fontWeight: 600, border: 'none', boxShadow: '0 10px 24px -8px rgba(199,83,26,0.5)' }}>Ver planos</button>}
        </div>
      ) : loading ? (
        <div style={{ padding: '20px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {[0, 1, 2, 3].map((i) => <div key={i} className="shimmer" style={{ aspectRatio: '2/3', borderRadius: 14, background: 'var(--paper-2)' }} />)}
        </div>
      ) : (porGenero || (clubs.length === 0 && !filtering)) ? (
        // Gênero escolhido some com os clubes: o pedido foi por livro daquele
        // assunto, e clube sem gênero embaixo seria ruído. Sem clube nenhum,
        // a tela também não anuncia o vazio — só cede o espaço pro acervo.

        null
      ) : filtering ? (
        <div style={{ paddingTop: 16 }}>
          <SectionHead title={q ? 'Clubes' : genre} sub={`${filtered.length} ${filtered.length === 1 ? 'clube' : 'clubes'}`} />
          {filtered.length === 0
            ? <div style={{ textAlign: 'center', padding: '40px 30px', color: 'var(--ink-3)' }}><div className="serif" style={{ fontSize: 18, color: 'var(--ink)', marginBottom: 6 }}>{q ? 'Nenhum clube com esse nome' : 'Nada por aqui ainda'}</div><div style={{ fontSize: 13.5 }}>{q ? 'Tente outro termo.' : 'Que tal criar o primeiro clube desse gênero?'}</div></div>
            : <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, padding: '0 18px' }}>{filtered.map((c) => <PosterCard key={c.id} club={c} onOpen={setOpen} />)}</div>}
        </div>
      ) : enough ? (
        <div style={{ paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 28 }}>
          {heroClub && (
            <div style={{ padding: '0 18px' }}>
              <button onClick={() => setOpen(heroClub)} style={{ display: 'block', width: '100%', padding: 0, border: 'none', background: 'none', position: 'relative', borderRadius: 18, overflow: 'hidden', aspectRatio: '4 / 3', boxShadow: '0 16px 40px -14px rgba(0,0,0,0.6)' }}>
                <ClubCover club={heroClub} kicker={heroClub.genre} landscape />
                <Badges club={heroClub} />
              </button>
            </div>
          )}
          <div>
            <SectionHead title="Em alta agora" sub="O que está movimentando a comunidade" />
            <div className="scroll-x" style={{ display: 'flex', gap: 13, overflowX: 'auto', padding: '0 18px' }}>
              {trending.map((c) => <div key={c.id} style={{ flexShrink: 0, width: 150 }}><PosterCard club={c} onOpen={setOpen} /></div>)}
            </div>
          </div>
          {news.length > 0 && (
            <div>
              <SectionHead title="Novos no Forjamente" sub="Recém-criados — entre antes de lotar" />
              <div className="scroll-x" style={{ display: 'flex', gap: 13, overflowX: 'auto', padding: '0 18px' }}>
                {news.map((c) => <div key={c.id} style={{ flexShrink: 0, width: 150 }}><PosterCard club={c} onOpen={setOpen} /></div>)}
              </div>
            </div>
          )}
          <div>
            <SectionHead title="Todos os clubes" sub={`${clubs.length} públicos`} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, padding: '0 18px' }}>{clubs.map((c) => <PosterCard key={c.id} club={c} onOpen={setOpen} />)}</div>
          </div>
        </div>
      ) : (
        <div style={{ paddingTop: 18 }}>
          <SectionHead title="Clubes públicos" sub={`${clubs.length} ${clubs.length === 1 ? 'clube' : 'clubes'} pra você entrar`} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, padding: '0 18px' }}>{clubs.map((c) => <PosterCard key={c.id} club={c} onOpen={setOpen} />)}</div>
        </div>
      )}

      {/* A estante curada fecha a Explorar. Quem chegou buscando gente já
          achou acima; quem chegou sem saber o que ler encontra livro em vez
          de "ainda não há clubes públicos". */}
      <AcervoMosaico genero={genre} busca={query} onAbrir={(slug) => setLivroAcervo(slug)}/>

      {open && <DetailSheet club={open} session={session} onClose={() => setOpen(null)} onOpenClub={(id: string) => { setOpen(null); onOpenClub(id); }} onUpgrade={onUpgrade} />}
    </div>
  );
};
